import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Topbar from "@/components/Topbar";
import { apiClient, API, formatDate, TASK_STATUS_LABELS, TASK_STATUS_META } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  UploadSimple,
  FilePdf,
  MagnifyingGlass,
  Printer,
  Camera,
  Warning,
  Trash,
  Play,
  CheckCircle,
  ShieldCheck,
  Wrench,
  UserCircle,
  X,
  Sparkle,
  ArrowClockwise,
  Package,
  ChatCircleText,
  PaperPlaneRight,
} from "@phosphor-icons/react";

const STATUS_ORDER = ["a_demonter", "pris_en_charge", "en_cours", "probleme", "demontee", "controlee"];

export default function Commandes() {
  const { user } = useAuth() || {};
  const isAdminOrResp = user && ["admin", "responsable"].includes(user.role);

  const [tab, setTab] = useState("active");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (tab && tab !== "all") params.status = tab;
      if (search.trim()) params.q = search.trim();
      const { data } = await apiClient.get("/commandes/tasks", { params });
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/commandes/stats");
      setStats(data);
    } catch (err) {
      console.warn("commandes/stats failed", err);
    }
  }, []);

  useEffect(() => { load(); loadStats(); }, [load, loadStats]);
  useEffect(() => {
    const id = setInterval(() => { load(); loadStats(); }, 20000);
    return () => clearInterval(id);
  }, [load, loadStats]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const k = t.transaction_number;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(t);
    }
    return Array.from(map.entries());
  }, [tasks]);

  const statusCount = stats?.tasks || {};

  return (
    <>
      <Topbar />
      <main className="px-6 py-8 max-w-7xl mx-auto" data-testid="page-commandes">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="label-tiny text-zinc-400">Atelier · Démontage</div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight mt-1">Commandes du jour</h1>
            <p className="text-sm text-zinc-500 mt-1.5">Importez les PDFs Opisto — l&apos;IA crée automatiquement une tâche par pièce à démonter.</p>
          </div>
          <Button
            onClick={() => setImportOpen(true)}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-11 gap-2 px-4"
            data-testid="btn-import-commandes"
          >
            <UploadSimple size={16} weight="bold" /> Importer les commandes du jour
          </Button>
        </div>

        {/* Status counters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
          {STATUS_ORDER.map((s) => {
            const meta = TASK_STATUS_META[s];
            const count = statusCount[s] || 0;
            return (
              <button
                key={s}
                onClick={() => setTab(s)}
                className={`text-left border p-3 transition ${tab === s ? "border-zinc-900 ring-1 ring-zinc-900" : "border-zinc-200 hover:border-zinc-400"} bg-white`}
                data-testid={`counter-${s}`}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                  {TASK_STATUS_LABELS[s]}
                </div>
                <div className="text-2xl font-heading font-semibold mt-1">{count}</div>
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher : n° transaction, LP, marque, modèle, pièce..."
              className="h-10 pl-9 rounded-sm border-zinc-300"
              data-testid="search-tasks"
            />
          </div>
          <div className="flex items-center gap-1 border border-zinc-200 rounded-sm bg-white p-1">
            {[
              { id: "active", label: "Actives" },
              { id: "all", label: "Toutes" },
              { id: "done", label: "Terminées" },
            ].map((v) => (
              <button
                key={v.id}
                onClick={() => setTab(v.id)}
                className={`px-3 h-8 text-xs font-medium rounded-sm ${tab === v.id ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-50"}`}
                data-testid={`tab-${v.id}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={() => { load(); loadStats(); }} className="rounded-sm h-10 gap-1.5" data-testid="btn-refresh-tasks">
            <ArrowClockwise size={14} weight="bold" /> Actualiser
          </Button>
        </div>

        {/* Tasks list */}
        {loading ? (
          <div className="bg-white border border-zinc-200 px-6 py-16 text-center text-sm text-zinc-500">Chargement...</div>
        ) : tasks.length === 0 ? (
          <div className="bg-white border border-zinc-200 px-6 py-16 text-center" data-testid="tasks-empty">
            <Package size={38} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
            <div className="text-sm text-zinc-500 mb-2">Aucune tâche</div>
            <div className="text-xs text-zinc-400">Importez un PDF Opisto pour créer automatiquement les tâches de démontage.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="tasks-list">
            {grouped.map(([tx, list]) => (
              <div key={tx} className="bg-white border border-zinc-200">
                <div className="px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/50 flex items-center gap-3 flex-wrap">
                  <div className="font-mono text-sm font-semibold" data-testid={`tx-${tx}`}>TRX {tx}</div>
                  <div className="text-xs text-zinc-500">
                    <span className="font-medium text-zinc-700">{list[0].brand || "—"}</span>{" "}
                    <span>{list[0].model || ""}</span>
                    {list[0].lp_number && (
                      <>
                        <span className="mx-2 text-zinc-300">·</span>
                        LP <span className="font-mono">{list[0].lp_number}</span>
                      </>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-400 ml-auto">{list.length} pièce{list.length > 1 ? "s" : ""}</div>
                </div>
                <div>
                  {list.map((t) => (
                    <TaskRow key={t.id} task={t} onOpen={() => setDetailTask(t)} onChanged={() => { load(); loadStats(); }} isAdminOrResp={isAdminOrResp} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onDone={() => { load(); loadStats(); }}
      />

      <TaskDetail
        task={detailTask}
        onOpenChange={(o) => !o && setDetailTask(null)}
        onChanged={(updated) => { setDetailTask(updated); load(); loadStats(); }}
        isAdminOrResp={isAdminOrResp}
      />
    </>
  );
}

function StatusChip({ status }) {
  const m = TASK_STATUS_META[status] || TASK_STATUS_META.a_demonter;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`}></span>
      {TASK_STATUS_LABELS[status] || status}
    </span>
  );
}

function TaskRow({ task, onOpen, onChanged, isAdminOrResp }) {
  const [busy, setBusy] = useState(false);

  const setStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      await apiClient.patch(`/commandes/tasks/${task.id}/status`, { status, ...extra });
      toast.success(TASK_STATUS_LABELS[status]);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-3 border-b border-zinc-100 last:border-b-0 flex items-center gap-3 flex-wrap sm:flex-nowrap hover:bg-zinc-50/50" data-testid={`task-row-${task.id}`}>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <div className="font-medium text-sm truncate">{task.designation}</div>
        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-zinc-500 flex-wrap">
          <span className="font-mono">Ref {task.opisto_ref || "—"}</span>
          {task.assignee_name && <span className="inline-flex items-center gap-1"><UserCircle size={11} weight="fill" />{task.assignee_name}</span>}
          {task.photo_id && <span className="text-blue-600 inline-flex items-center gap-1"><Camera size={11} weight="fill" />photo</span>}
          {task.problem_note && <span className="text-fuchsia-700 truncate max-w-[300px]">⚠ {task.problem_note}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusChip status={task.status} />
        <div className="hidden md:flex items-center gap-1">
          {task.status === "a_demonter" && (
            <Button size="sm" onClick={() => setStatus("pris_en_charge")} disabled={busy} className="h-8 rounded-sm gap-1 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid={`btn-take-${task.id}`}>
              <Wrench size={12} weight="bold" /> Prendre
            </Button>
          )}
          {task.status === "pris_en_charge" && (
            <Button size="sm" onClick={() => setStatus("en_cours")} disabled={busy} className="h-8 rounded-sm gap-1 bg-orange-600 hover:bg-orange-700 text-white text-xs" data-testid={`btn-start-${task.id}`}>
              <Play size={12} weight="bold" /> Démarrer
            </Button>
          )}
          {task.status === "en_cours" && (
            <Button size="sm" onClick={() => setStatus("demontee")} disabled={busy} className="h-8 rounded-sm gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid={`btn-demontee-${task.id}`}>
              <CheckCircle size={12} weight="bold" /> Démontée
            </Button>
          )}
          {task.status === "demontee" && isAdminOrResp && (
            <Button size="sm" onClick={() => setStatus("controlee")} disabled={busy} className="h-8 rounded-sm gap-1 bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`btn-controlee-${task.id}`}>
              <ShieldCheck size={12} weight="bold" /> Contrôler
            </Button>
          )}
        </div>
        <a
          href={`${API}/commandes/tasks/${task.id}/label`}
          target="_blank"
          rel="noreferrer"
          className="p-2 hover:bg-zinc-100 text-zinc-700"
          title="Imprimer l'étiquette"
          data-testid={`btn-print-${task.id}`}
        >
          <Printer size={14} weight="bold" />
        </a>
      </div>
    </div>
  );
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  useEffect(() => { if (open) { setFiles([]); setResult(null); } }, [open]);

  const addFiles = (list) => {
    const pdfs = Array.from(list).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length !== list.length) toast.error("Seuls les PDFs sont acceptés");
    setFiles((prev) => [...prev, ...pdfs]);
  };

  const submit = async () => {
    if (!files.length) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await apiClient.post("/commandes/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setResult(data);
      if (data.tasks_created > 0) {
        toast.success(`✨ ${data.tasks_created} tâche(s) créée(s) par l'IA`);
        onDone && onDone();
      } else if (data.errors?.length) {
        toast.error("Import échoué — voir détails");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec import");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-amber-500" /> Importer les commandes du jour
          </DialogTitle>
          <DialogDescription>
            Déposez un ou plusieurs PDF Opisto. L&apos;IA (Gemini) extrait automatiquement les pièces et crée les tâches de démontage.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition ${dragging ? "border-zinc-900 bg-zinc-50" : "border-zinc-300 hover:border-zinc-500"}`}
            data-testid="dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
              data-testid="file-input"
            />
            <UploadSimple size={30} weight="duotone" className="mx-auto text-zinc-400 mb-2" />
            <div className="font-medium">Glissez vos PDFs ici</div>
            <div className="text-xs text-zinc-500 mt-1">ou cliquez pour sélectionner — plusieurs fichiers acceptés</div>
          </div>
        )}

        {!result && files.length > 0 && (
          <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="selected-files">
            {files.map((f) => {
              const key = `${f.name}-${f.size}-${f.lastModified}`;
              return (
                <div key={key} className="flex items-center gap-2 px-2 py-1.5 border border-zinc-200 text-sm">
                  <FilePdf size={16} weight="fill" className="text-red-600 shrink-0" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-[11px] text-zinc-400">{(f.size / 1024).toFixed(0)} Ko</span>
                  <button
                    onClick={() => setFiles(files.filter((x) => x !== f))}
                    className="text-zinc-400 hover:text-red-600"
                    data-testid={`remove-file-${f.name}`}
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {uploading && (
          <div className="text-center py-8" data-testid="uploading">
            <Sparkle size={30} className="mx-auto text-amber-500 animate-pulse" weight="fill" />
            <div className="mt-3 font-medium text-sm">Analyse IA en cours...</div>
            <div className="text-xs text-zinc-500 mt-1">Gemini extrait les pièces — cela peut prendre 20-60 secondes.</div>
          </div>
        )}

        {result && (
          <div className="space-y-2" data-testid="import-result">
            <div className="bg-green-50 border border-green-200 p-3 text-sm">
              <div className="font-semibold text-green-900">
                ✨ {result.tasks_created} tâche(s) créée(s) dans {result.orders_created} commande(s)
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 p-3 text-xs">
                <div className="font-semibold text-amber-900 mb-1">Avertissements :</div>
                {result.errors.map((e) => (
                  <div key={`${e.file}-${e.error}`} className="text-amber-800">• <span className="font-mono">{e.file}</span> — {e.error}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading} className="rounded-sm">
            {result ? "Fermer" : "Annuler"}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={!files.length || uploading} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-1.5" data-testid="btn-submit-import">
              <Sparkle size={14} weight="fill" /> {uploading ? "Analyse..." : `Analyser ${files.length || ""} fichier${files.length > 1 ? "s" : ""}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskDetail({ task, onOpenChange, onChanged, isAdminOrResp }) {
  const [problemOpen, setProblemOpen] = useState(false);
  const [problemNote, setProblemNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoRef = useRef();

  useEffect(() => {
    if (task && isAdminOrResp) {
      apiClient.get("/auth/users").then(({ data }) => setAssignees(data.filter((u) => u.is_active !== false && u.role !== "client")));
    }
  }, [task, isAdminOrResp]);

  if (!task) return null;

  const setStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await apiClient.patch(`/commandes/tasks/${task.id}/status`, { status, ...extra });
      toast.success(TASK_STATUS_LABELS[status]);
      onChanged && onChanged(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    finally { setBusy(false); }
  };

  const assign = async (userId) => {
    try {
      const { data } = await apiClient.patch(`/commandes/tasks/${task.id}/assign`, { assignee_id: userId || null });
      toast.success(userId ? `Assignée à ${data.assignee_name}` : "Désassignée");
      onChanged && onChanged({ ...task, ...data });
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await apiClient.post(`/commandes/tasks/${task.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo enregistrée");
      const { data } = await apiClient.get(`/commandes/tasks/${task.id}`);
      onChanged && onChanged(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Échec upload"); }
    finally { setPhotoBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer définitivement cette tâche ?")) return;
    try {
      await apiClient.delete(`/commandes/tasks/${task.id}`);
      toast.success("Tâche supprimée");
      onOpenChange(false);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  return (
    <>
      <Dialog open={!!task} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-sm sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wrench size={18} weight="duotone" />
              <span className="font-mono">TRX {task.transaction_number}</span>
              <StatusChip status={task.status} />
            </DialogTitle>
            <DialogDescription>Tâche de démontage · créée le {formatDate(task.created_at)}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="LP véhicule" value={task.lp_number} mono />
            <Field label="Ref Opisto" value={task.opisto_ref} mono />
            <Field label="Marque" value={task.brand} />
            <Field label="Modèle" value={task.model} />
          </div>
          <div className="border-t border-zinc-200 pt-3">
            <div className="label-tiny text-zinc-500">Pièce à démonter</div>
            <div className="font-semibold text-base mt-1">{task.designation}</div>
          </div>
          {task.assignee_name && (
            <div className="text-sm flex items-center gap-2">
              <UserCircle size={14} weight="fill" className="text-zinc-500" />
              Assignée à <span className="font-medium">{task.assignee_name}</span>
            </div>
          )}
          {task.problem_note && (
            <div className="bg-fuchsia-50 border border-fuchsia-200 p-2.5 text-sm">
              <div className="flex items-center gap-1.5 text-fuchsia-800 font-medium text-xs uppercase tracking-wider">
                <Warning size={12} weight="fill" /> Problème signalé
              </div>
              <div className="mt-1 text-fuchsia-900">{task.problem_note}</div>
            </div>
          )}

          {/* Photo */}
          {task.photo_id ? (
            <div>
              <div className="label-tiny text-zinc-500 mb-1">Photo</div>
              <img src={`${API}/commandes/tasks/${task.id}/photo`} alt="task" className="max-h-56 border border-zinc-200" data-testid="task-photo" />
            </div>
          ) : null}
          <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} />

          {/* Assignment */}
          {isAdminOrResp && (
            <div className="border-t border-zinc-200 pt-3">
              <Label className="label-tiny text-zinc-500">Assignation (admin/responsable)</Label>
              <Select value={task.assignee_id || "none"} onValueChange={(v) => assign(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 rounded-sm border-zinc-300 mt-1.5" data-testid="assign-select">
                  <SelectValue placeholder="Non assignée" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Non assignée —</SelectItem>
                  {assignees.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Commentaires internes */}
          <TaskComments taskId={task.id} />

          {/* Actions */}
          <div className="border-t border-zinc-200 pt-3 flex flex-wrap gap-2">
            {task.status === "a_demonter" && (
              <Button onClick={() => setStatus("pris_en_charge")} disabled={busy} className="rounded-sm gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="btn-detail-take">
                <Wrench size={13} weight="bold" /> Prendre en charge
              </Button>
            )}
            {task.status === "pris_en_charge" && (
              <Button onClick={() => setStatus("en_cours")} disabled={busy} className="rounded-sm gap-1.5 bg-orange-600 hover:bg-orange-700" data-testid="btn-detail-start">
                <Play size={13} weight="bold" /> Démarrer démontage
              </Button>
            )}
            {(task.status === "en_cours" || task.status === "pris_en_charge") && (
              <Button onClick={() => setStatus("demontee")} disabled={busy} className="rounded-sm gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="btn-detail-done">
                <CheckCircle size={13} weight="bold" /> Démontée
              </Button>
            )}
            {task.status === "demontee" && isAdminOrResp && (
              <Button onClick={() => setStatus("controlee")} disabled={busy} className="rounded-sm gap-1.5 bg-green-600 hover:bg-green-700" data-testid="btn-detail-controlled">
                <ShieldCheck size={13} weight="bold" /> Marquer contrôlée
              </Button>
            )}
            <Button variant="outline" onClick={() => setProblemOpen(true)} className="rounded-sm gap-1.5 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50" data-testid="btn-detail-problem">
              <Warning size={13} weight="bold" /> Signaler un problème
            </Button>
            <Button variant="outline" onClick={() => photoRef.current?.click()} disabled={photoBusy} className="rounded-sm gap-1.5" data-testid="btn-detail-photo">
              <Camera size={13} weight="bold" /> {photoBusy ? "..." : task.photo_id ? "Remplacer photo" : "Ajouter photo"}
            </Button>
            <a
              href={`${API}/commandes/tasks/${task.id}/label`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-300 hover:bg-zinc-50 text-xs font-medium"
              data-testid="btn-detail-print"
            >
              <Printer size={13} weight="bold" /> Imprimer étiquette
            </a>
            {isAdminOrResp && (
              <Button variant="outline" onClick={del} className="rounded-sm gap-1.5 border-red-300 text-red-600 hover:bg-red-50 ml-auto" data-testid="btn-detail-delete">
                <Trash size={13} weight="bold" /> Supprimer
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Problem dialog */}
      <Dialog open={problemOpen} onOpenChange={setProblemOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="problem-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Warning size={18} weight="fill" className="text-fuchsia-600" /> Signaler un problème</DialogTitle>
            <DialogDescription>Décrivez brièvement le souci constaté sur cette pièce.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={problemNote}
            onChange={(e) => setProblemNote(e.target.value)}
            placeholder="Ex: Pièce cassée, absente, mauvaise référence, difficulté d'accès..."
            className="rounded-sm border-zinc-300 min-h-[110px]"
            data-testid="problem-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setProblemOpen(false)} className="rounded-sm">Annuler</Button>
            <Button
              onClick={async () => { await setStatus("probleme", { problem_note: problemNote }); setProblemOpen(false); setProblemNote(""); }}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 rounded-sm gap-1.5"
              data-testid="btn-submit-problem"
            >
              <Warning size={13} weight="bold" /> Signaler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TaskComments({ taskId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    apiClient.get(`/commandes/tasks/${taskId}/comments`).then(({ data }) => setComments(data)).catch(() => {});
  }, [taskId]);

  const send = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const { data } = await apiClient.post(`/commandes/tasks/${taskId}/comments`, { content });
      setComments((c) => [...c, data]);
      setText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
    finally { setSending(false); }
  };

  return (
    <div className="border-t border-zinc-200 pt-3" data-testid="task-comments">
      <div className="label-tiny text-zinc-500 flex items-center gap-1.5">
        <ChatCircleText size={13} weight="duotone" /> Commentaires internes {comments.length > 0 && `(${comments.length})`}
      </div>
      {comments.length > 0 && (
        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-1" data-testid="task-comments-list">
          {comments.map((c) => (
            <div key={c.id} className="bg-zinc-50 border border-zinc-200 px-2.5 py-2" data-testid={`task-comment-${c.id}`}>
              <div className="flex items-center justify-between gap-2 text-[11px] text-zinc-500">
                <span className="font-medium text-zinc-700">{c.author_name}</span>
                <span className="font-mono">{formatDate(c.created_at)}</span>
              </div>
              <div className="text-sm mt-0.5 whitespace-pre-wrap">{c.content}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-end gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ajouter un commentaire visible par toute l'équipe..."
          className="rounded-sm border-zinc-300 min-h-[38px] max-h-28 text-sm flex-1"
          data-testid="task-comment-input"
        />
        <Button onClick={send} disabled={sending || !text.trim()} className="rounded-sm h-9 gap-1.5 shrink-0" data-testid="task-comment-send">
          <PaperPlaneRight size={13} weight="bold" /> Envoyer
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="label-tiny text-zinc-500">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono" : "font-medium"} ${value ? "text-zinc-900" : "text-zinc-300"}`}>{value || "—"}</div>
    </div>
  );
}
